/* eslint-disable */
import React, { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import ReactPaginate from "react-paginate";
import { Search, Banknote, ExternalLink } from "lucide-react";
import { useHistory } from "react-router-dom";
import RenderSupportPage from "../RenderSupportPage";
import PageSkeleton from "../../Components/PageSkeleton";
import { dataActionCreators } from "../../services/Actions";
import { GhanaMoneyDisplay } from "../../helpers/helper";

// Loan-deduction lookup for the support desk, mirroring the loans module's
// deduction page (/admin/loans → GetAllLoans → /loans). Read-only: an agent
// answers "was my loan deducted this month, and what's my balance?" and can
// jump to the member's full profile from any row.

function LoanDeductionLookup() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { GetAllLoans } = bindActionCreators(dataActionCreators, dispatch);

  const data = useSelector((state) => state?.data);
  const { loans, paginate, isLoading, total_loans } = data;

  const [search, setSearch] = useState(null);
  const [limit, setLimit] = useState(30);
  const [pageCount, setPageCount] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    Promise.resolve(GetAllLoans(true, 1, limit)).finally(() =>
      setHasLoaded(true)
    );
  }, [limit]);

  useEffect(() => {
    setPageCount(Number(paginate?.total) || 0);
  }, [paginate]);

  const handlePageClick = (event) => {
    GetAllLoans(
      true,
      event.selected + 1,
      limit,
      search ? `&search=${search}` : ""
    );
  };

  const onSearchTextChange = debounce((e) => {
    const value = e?.target?.value;
    setSearch(value || null);
    GetAllLoans(true, 1, limit, value ? `&search=${value}` : "");
  }, 800);

  const rows = loans || [];

  return (
    <RenderSupportPage title="Loan Deduction">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-5">
        Search a member's monthly loan deductions and running balance by staff ID.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="search"
            onChange={onSearchTextChange}
            placeholder="Search by staff ID"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200"
        >
          {[30, 50, 100, 250].map((n) => (
            <option key={n} value={n}>{n} per page</option>
          ))}
        </select>

        {total_loans != null && (
          <span className="ml-auto px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-sm font-bold">
            Total: <GhanaMoneyDisplay amount={total_loans ?? 0} />
          </span>
        )}
      </div>

      {!hasLoaded && rows.length === 0 ? (
        <PageSkeleton cards={0} rows={8} cols={6} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                <tr>
                  {["Member", "Deduction", "Balance", "Period", "Transaction", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Banknote size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                        No loan deductions found
                      </p>
                    </td>
                  </tr>
                ) : (
                  rows.map((loan) => (
                    <tr key={loan?._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <img
                            className="w-8 h-8 rounded-full border border-slate-200 dark:border-gray-600 object-cover"
                            src={loan?.user?.profile_image || "/images/dev/avarta2.jpeg"}
                            alt="Member"
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                              {loan?.user?.name || "—"}
                            </p>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500">
                              {loan?.staff_id || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          <GhanaMoneyDisplay amount={loan?.deduction ?? 0} />
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        {loan?.balance != null ? (
                          <GhanaMoneyDisplay amount={loan.balance} />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {loan?.period || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                        {loan?.transaction_type || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {loan?.staff_id && (
                          <button
                            title="Open member profile"
                            onClick={() =>
                              history.push(`/support/member-detailed?staff_id=${loan.staff_id}`)
                            }
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-700 rounded-lg transition-all"
                          >
                            <ExternalLink size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="border-t border-slate-100 dark:border-gray-700">
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                previousLabel="<"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageCount}
                renderOnZeroPageCount={null}
                containerClassName="flex items-center justify-center gap-1 py-4"
                pageLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 transition-colors"
                previousLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                nextLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                activeLinkClassName="!bg-indigo-600 !text-white shadow-sm"
              />
            </div>
          )}
        </div>
      )}
    </RenderSupportPage>
  );
}

export default LoanDeductionLookup;
